import * as THREE from 'three';

/** Drop the stamped FPS hold from the eyes toward the chest. */
export const HOLD_DROP = 0.16;
/** Push the hold toward the viewer. Hip sits closer; ADS reaches out. */
export const HOLD_FORWARD_HIP = 0.16;
export const HOLD_FORWARD_ADS = 0.38;
/** Character local +X is the gunner's right. */
export const HOLD_SIDE = 0.04;
/** Hand local +Z is the wrist / sleeve cuff (back of the FPS lower arm). */
export const CUFF_BACK_Z = 0.28;

const _bridgeA = new THREE.Vector3();
const _bridgeB = new THREE.Vector3();
const _bridgeMid = new THREE.Vector3();
const _bridgeDir = new THREE.Vector3();
const _bridgeUp = new THREE.Vector3(0, 1, 0);

export function hideBodyArms(bones: Record<string, THREE.Object3D> | undefined, hide: boolean): void {
  if (!bones) return;
  if (bones.LeftArm) bones.LeftArm.visible = !hide;
  if (bones.RightArm) bones.RightArm.visible = !hide;
}

export function findClonedHand(hold: THREE.Object3D, isRight: boolean): THREE.Object3D | null {
  const named = hold.getObjectByName(isRight ? 'procHandR' : 'procHandL');
  if (named) return named;
  const grip = hold.getObjectByName(isRight ? 'grip_r' : 'grip_l');
  if (!grip) return null;
  let best: THREE.Object3D | null = null;
  for (const c of grip.children) {
    if ((c as THREE.Mesh).isMesh) continue;
    if (!best || c.children.length > best.children.length) best = c;
  }
  return best;
}

export function placeUpperArm(
  mesh: THREE.Mesh,
  parent: THREE.Object3D,
  fromWorld: THREE.Vector3,
  toWorld: THREE.Vector3,
): number {
  _bridgeA.copy(fromWorld);
  _bridgeB.copy(toWorld);
  parent.worldToLocal(_bridgeA);
  parent.worldToLocal(_bridgeB);
  _bridgeDir.subVectors(_bridgeB, _bridgeA);
  const len = _bridgeDir.length();
  if (len < 0.05) {
    mesh.visible = false;
    return 0;
  }
  _bridgeDir.multiplyScalar(1 / len);
  _bridgeA.addScaledVector(_bridgeDir, 0.055);
  _bridgeB.addScaledVector(_bridgeDir, -0.02);
  const span = _bridgeA.distanceTo(_bridgeB);
  if (span < 0.04) {
    mesh.visible = false;
    return 0;
  }
  mesh.visible = true;
  _bridgeMid.copy(_bridgeA).add(_bridgeB).multiplyScalar(0.5);
  mesh.position.copy(_bridgeMid);
  mesh.quaternion.setFromUnitVectors(_bridgeUp, _bridgeDir);
  mesh.scale.set(1, span, 1);
  return span;
}

export function placeShoulder(mesh: THREE.Mesh, parent: THREE.Object3D, worldPos: THREE.Vector3): void {
  _bridgeA.copy(worldPos);
  parent.worldToLocal(_bridgeA);
  mesh.position.copy(_bridgeA);
  mesh.visible = true;
}

export function createJacketBridges(sleeveHex: number): {
  upperArmR: THREE.Mesh;
  upperArmL: THREE.Mesh;
  shoulderR: THREE.Mesh;
  shoulderL: THREE.Mesh;
} {
  const sleeve = new THREE.MeshBasicMaterial({ color: sleeveHex, fog: true });
  const limbGeo = new THREE.CylinderGeometry(0.044, 0.07, 1, 8);
  const shoulderGeo = new THREE.SphereGeometry(0.078, 10, 8);
  const upperArmR = new THREE.Mesh(limbGeo, sleeve);
  const upperArmL = new THREE.Mesh(limbGeo, sleeve);
  const shoulderR = new THREE.Mesh(shoulderGeo, sleeve);
  const shoulderL = new THREE.Mesh(shoulderGeo, sleeve);
  upperArmR.name = 'avatarUpperArmR';
  upperArmL.name = 'avatarUpperArmL';
  shoulderR.name = 'avatarShoulderR';
  shoulderL.name = 'avatarShoulderL';
  for (const m of [upperArmR, upperArmL, shoulderR, shoulderL]) {
    m.frustumCulled = false;
    m.castShadow = false;
    m.visible = false;
  }
  return { upperArmR, upperArmL, shoulderR, shoulderL };
}

const _shoulderW = new THREE.Vector3();
const _chestW = new THREE.Vector3();
const _cuffW = new THREE.Vector3();

export function updateJacketBridges(
  hold: THREE.Object3D | null,
  bones: Record<string, THREE.Object3D> | undefined,
  parent: THREE.Object3D,
  upperArmR: THREE.Mesh,
  upperArmL: THREE.Mesh,
  shoulderR: THREE.Mesh,
  shoulderL: THREE.Mesh,
): void {
  if (!hold || !bones) {
    upperArmR.visible = false;
    upperArmL.visible = false;
    shoulderR.visible = false;
    shoulderL.visible = false;
    return;
  }
  parent.updateWorldMatrix(true, false);
  hold.updateWorldMatrix(true, true);
  bones.Spine2?.updateWorldMatrix(true, false);
  const handR = findClonedHand(hold, true);
  const handL = findClonedHand(hold, false);
  if (handR?.visible && bones.RightArm) {
    bones.RightArm.getWorldPosition(_shoulderW);
    if (bones.Spine2) {
      bones.Spine2.getWorldPosition(_chestW);
      _shoulderW.lerp(_chestW, 0.28);
    }
    placeShoulder(shoulderR, parent, _shoulderW);
    handR.localToWorld(_cuffW.set(0, 0, CUFF_BACK_Z));
    placeUpperArm(upperArmR, parent, _shoulderW, _cuffW);
  } else {
    upperArmR.visible = false;
    shoulderR.visible = false;
  }
  if (handL?.visible && bones.LeftArm) {
    bones.LeftArm.getWorldPosition(_shoulderW);
    if (bones.Spine2) {
      bones.Spine2.getWorldPosition(_chestW);
      _shoulderW.lerp(_chestW, 0.28);
    }
    placeShoulder(shoulderL, parent, _shoulderW);
    handL.localToWorld(_cuffW.set(0, 0, CUFF_BACK_Z));
    placeUpperArm(upperArmL, parent, _shoulderW, _cuffW);
  } else {
    upperArmL.visible = false;
    shoulderL.visible = false;
  }
}

/** Same eye-local rest as the clone: fists sit closer and a bit higher than guns. */
export function placeHoldEye(
  eye: THREE.Object3D,
  ads: number,
  fists: boolean,
  lookPitch: number,
  kneelBlend: number,
): void {
  const holdFwd = fists
    ? THREE.MathUtils.lerp(-0.05, 0.0, ads)
    : THREE.MathUtils.lerp(HOLD_FORWARD_HIP, HOLD_FORWARD_ADS, ads);
  const holdDrop = fists ? HOLD_DROP - 0.08 : HOLD_DROP;
  const eyeDrop = 0.34 * kneelBlend;
  eye.position.set(HOLD_SIDE, 1.62 - holdDrop - eyeDrop, -holdFwd);
  eye.rotation.order = 'YXZ';
  eye.rotation.set(lookPitch, 0, 0);
}

/** Match `Viewmodel.cloneHoldVisual`: world holds use unlit Basic copies. */
export function flattenHoldMaterials(root: THREE.Object3D): void {
  root.traverse((o) => {
    if ((o as THREE.Light).isLight) {
      o.visible = false;
      return;
    }
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = false;
    mesh.frustumCulled = false;
    const mat = mesh.material;
    if (mat instanceof THREE.MeshStandardMaterial) {
      mesh.material = new THREE.MeshBasicMaterial({
        color: mat.color.clone(),
        map: mat.map,
        fog: true,
        transparent: mat.transparent,
        opacity: mat.opacity,
      });
    }
  });
}
